const calendarFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit'
});
const clockFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
});
const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

export function santiagoDate(timestamp = Date.now()) {
  const parts = Object.fromEntries(calendarFormatter.formatToParts(timestamp).map(p => [p.type, p.value]));
  return parts.year + '-' + parts.month + '-' + parts.day;
}

export function calendarDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(value + 'T12:00:00Z');
  if (!Number.isFinite(date.valueOf()) || date.toISOString().slice(0, 10) !== value) return null;
  return { iso: value, year: Number(value.slice(0, 4)), month: Number(value.slice(5, 7)), day: Number(value.slice(8, 10)) };
}

export function selectEstimate(entries, today, capacityExceeded = false) {
  if (capacityExceeded) return { status: 'capacity', eligible: [] };
  const eligible = [];
  for (const entry of entries) {
    const start = calendarDate(entry.start);
    const end = entry.end == null || entry.end === '' ? start : calendarDate(entry.end);
    if (!start || !end || start.iso > end.iso || end.iso < today) continue;
    if (!eligible.some(e => e.id === entry.id)) eligible.push({ id: entry.id, start, end });
  }
  return { status: eligible.length > 1 ? 'conflict' : eligible.length === 1 ? 'visible' : 'empty', eligible };
}

export function estimateCopy(estimate, today) {
  const { start, end } = estimate;
  const currentYear = Number(today.slice(0, 4));
  const dateWords = (date, year = false) => date.day + ' de ' + months[date.month - 1] + (year ? ' de ' + date.year : '');
  if (start.iso === end.iso) return 'Esperamos contar con stock el ' + dateWords(start, start.year !== currentYear) + '.';
  if (start.year === end.year && start.month === end.month) {
    return 'Esperamos contar con stock entre el ' + start.day + ' y el ' + dateWords(end, end.year !== currentYear) + '.';
  }
  const crossYear = start.year !== end.year;
  return 'Esperamos contar con stock entre el ' + dateWords(start, crossYear) + ' y el ' + dateWords(end, crossYear || end.year !== currentYear) + '.';
}

export function compactEstimateParts(estimate, today) {
  const { start, end } = estimate;
  const currentYear = Number(today.slice(0, 4));
  const dateWords = (date, year = false) => date.day + ' de ' + months[date.month - 1] + (year ? ' de ' + date.year : '');
  if (start.iso === end.iso) return { preposition: 'el ', date: dateWords(start, start.year !== currentYear) };
  if (start.year === end.year && start.month === end.month) {
    return { preposition: 'entre el ', date: start.day + ' y el ' + dateWords(end, end.year !== currentYear) };
  }
  const crossYear = start.year !== end.year;
  return { preposition: 'entre el ', date: dateWords(start, crossYear) + ' y el ' + dateWords(end, crossYear || end.year !== currentYear) };
}

export function nextSantiagoDay(timestamp = Date.now()) {
  const tomorrow = new Date(santiagoDate(timestamp) + 'T12:00:00Z');
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  const target = Date.UTC(tomorrow.getUTCFullYear(), tomorrow.getUTCMonth(), tomorrow.getUTCDate());
  let guess = target;
  for (let i = 0; i < 4; i++) {
    const p = Object.fromEntries(clockFormatter.formatToParts(guess).map(part => [part.type, Number(part.value)]));
    const local = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    const delta = target - local;
    if (!delta) break;
    guess += delta;
  }
  return guess;
}

if (typeof HTMLElement !== 'undefined' && !customElements.get('rn-restock-notice')) {
  customElements.define('rn-restock-notice', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.hidden = true;
      this.abort = new AbortController();
      const { signal } = this.abort;
      try { this.entries = JSON.parse(this.querySelector('[data-restock-batches]').textContent); } catch { this.entries = []; }
      document.addEventListener('visibilitychange', () => this.update(), { signal });
      window.addEventListener('pageshow', () => this.update(), { signal });
      this.update();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      clearTimeout(this.timer);
    }
    update() {
      clearTimeout(this.timer);
      if (!this.isConnected || !this.closest('.product-information .product-details')) { this.hidden = true; return; }
      const today = santiagoDate();
      const result = selectEstimate(this.entries, today, this.dataset.capacityExceeded === 'true');
      this.dataset.status = result.status;
      const panel = this.querySelector('.rn-restock-panel');
      const admin = this.querySelector('[data-restock-admin]');
      panel.hidden = result.status !== 'visible';
      if (admin) {
        admin.hidden = !['conflict', 'capacity'].includes(result.status);
        admin.textContent = result.status === 'conflict'
          ? 'Conflicto de reposición: este producto tiene más de un lote activo con fechas vigentes. Deja un solo lote activo para mostrar el aviso.'
          : 'No se muestra el aviso: esta definición supera 250 lotes. Revisa y retira las entradas históricas de esta definición antes de continuar.';
      }
      this.hidden = panel.hidden && (!admin || admin.hidden);
      if (result.status === 'visible') {
        const copy = this.querySelector('[data-restock-copy]');
        if (this.dataset.design === 'compact') {
          const parts = compactEstimateParts(result.eligible[0], today);
          const prefix = (copy.dataset.restockPrefix || 'Stock estimado') + ' ' + parts.preposition;
          const text = prefix + parts.date + '.';
          if (copy.textContent !== text) {
            const date = document.createElement('strong');
            date.textContent = parts.date;
            copy.replaceChildren(document.createTextNode(prefix), date, document.createTextNode('.'));
          }
        } else {
          const text = estimateCopy(result.eligible[0], today);
          if (copy.textContent !== text) copy.textContent = text;
        }
        this.dataset.batchId = result.eligible[0].id;
      } else {
        delete this.dataset.batchId;
      }
      if (result.status === 'conflict' && !this.conflictReported) {
        console.warn('Ruta Natural: estimaciones de reposición en conflicto para el producto ' + this.dataset.productId);
        this.conflictReported = true;
      }
      const delay = Math.min(60000, Math.max(50, nextSantiagoDay() - Date.now() + 25));
      this.timer = setTimeout(() => this.update(), delay);
    }
  });
}
